/**
 * Shop Service
 * Handles shop management (CRUD, status, activation)
 */

import { Shop, ShopExtended } from '../types/shop';
import { partnerRepository } from '../../data/partnerRepository';
import { shopQueries, shopMutations } from '../../data/shops';
import { QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { logger } from '../../infrastructure/logger';

interface ShopUpdateRequest {
    shopId: string;
    updates: Partial<Shop>;
}

export const shopService = {
    /**
     * Get shop by ID
     */
    async getShopById(shopId: string): Promise<ShopExtended | undefined> {
        return shopQueries.getShopById(shopId);
    },

    /**
     * Get shop by slug
     */
    async getShopBySlug(slug: string): Promise<Shop | undefined> {
        return partnerRepository.getShopBySlug(slug);
    },

    /**
     * Get shop by partner ID
     */
    async getShopByPartner(partnerId: string): Promise<ShopExtended | undefined> {
        return shopQueries.getShopByPartner(partnerId);
    },

    /**
     * Get active shops
     */
    async getActiveShops(limit: number = 50): Promise<Shop[]> {
        const result = await partnerRepository.getAllShops(limit);
        return result.shops.filter(s => s.status === 'active');
    },

    /**
     * Get featured shops
     */
    async getFeaturedShops(limit: number = 10): Promise<ShopExtended[]> {
        return shopQueries.getFeaturedShops(limit);
    },

    /**
     * Get shops by category
     */
    async getShopsByCategory(category: string): Promise<ShopExtended[]> {
        return shopQueries.getShopsByCategory(category);
    },

    /**
     * Search shops
     */
    async searchShops(queryStr: string, limit: number = 20): Promise<ShopExtended[]> {
        return shopQueries.searchShops(queryStr, limit);
    },

    /**
     * Get multiple shops by IDs
     */
    async getShopsByIds(shopIds: string[]): Promise<ShopExtended[]> {
        return shopQueries.getShopsByIds(shopIds);
    },

    /**
     * Get all shops with pagination
     */
    async getAllShops(
        pageSize?: number,
        filters?: { featured?: boolean; verified?: boolean },
        lastDoc?: QueryDocumentSnapshot<DocumentData> | null
    ): Promise<{ shops: Shop[], lastDoc: QueryDocumentSnapshot<DocumentData> | null }> {
        return partnerRepository.getAllShops(pageSize, filters, lastDoc);
    },

    /**
     * Get pending shops (for admin)
     */
    async getPendingShops(): Promise<Shop[]> {
        return partnerRepository.getPendingShops();
    },

    /**
     * Update shop
     */
    async updateShop({ shopId, updates }: ShopUpdateRequest): Promise<{ success: boolean; error?: string }> {
        try {
            await partnerRepository.updateShop(shopId, updates);
            return { success: true };
        } catch (error) {
            logger.error('[ShopService] Error updating shop', error);
            return { success: false, error: "Error al actualizar la tienda" };
        }
    },

    /**
     * Create shop
     */
    async createShop(shop: Shop): Promise<string> {
        return partnerRepository.createShop(shop);
    },

    /**
     * Approve shop
     */
    async approveShop(shopId: string, approvedBy: string): Promise<{ success: boolean; error?: string }> {
        try {
            await shopMutations.approveShop(shopId, approvedBy);
            return { success: true };
        } catch (error) {
            logger.error('[ShopService] Error approving shop', error);
            return { success: false, error: "Error al aprobar la tienda" };
        }
    },

    /**
     * Reject shop
     */
    async rejectShop(shopId: string, reason: string): Promise<{ success: boolean; error?: string }> {
        try {
            await shopMutations.rejectShop(shopId, reason);
            return { success: true };
        } catch (error) {
            logger.error('[ShopService] Error rejecting shop', error);
            return { success: false, error: "Error al rechazar la tienda" };
        }
    },

    /**
     * Delete shop
     */
    async deleteShop(shopId: string): Promise<{ success: boolean; error?: string }> {
        try {
            await shopMutations.deleteShop(shopId);
            return { success: true };
        } catch (error) {
            logger.error('[ShopService] Error deleting shop', error);
            return { success: false, error: "Error al eliminar la tienda" };
        }
    },

    /**
     * Get shop custom domain
     */
    async getCustomDomainShop(host: string): Promise<ShopExtended | undefined> {
        return shopQueries.getCustomDomainShop(host);
    },

    /**
     * Get shop banners
     */
    async getBanners(shopId: string) {
        return shopQueries.getBanners(shopId);
    },

    /**
     * Get shop stats
     */
    async getShopStats(shopId: string) {
        // This would call shopStats.getShopStats
        return null;
    }
};
